import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, useAnimation } from 'framer-motion';
import { 
  Payment,
  CreditCard,
  Security,
  TrendingUp,
  AccountBalance,
  Receipt,
  Analytics,
  CloudDone,
  ArrowForward,
  Check,
  Shield,
  Speed,
  CreditScore,
  Payments as PaymentsIcon,
  ReceiptLong,
  PieChart,
  AccountBalanceWallet,
  Bolt,
  AutoGraph,
  CurrencyExchange,
  LocalFireDepartment,
  FlashOn,
  PointOfSale,
  AttachMoney,
  CreditScore as CreditScoreIcon,
  Paid,
  BusinessCenter,
  Devices
} from '@mui/icons-material';
import { useInView } from 'react-intersection-observer';
import { AnimatePresence } from 'framer-motion';
import { 
  Storefront,
  Buildings,
  Bank
} from 'phosphor-react';
import { Helmet } from 'react-helmet-async';

const moveBackground = keyframes`
  0% { background-position: 0% 0%; }
  50% { background-position: 100% 100%; }
  100% { background-position: 0% 0%; }
`;

const PageContainer = styled.div`
  padding-top: 0;
  min-height: 100vh;
  overflow: hidden;
  background: #ffffff;
  color: #333;
  position: relative;
`;

const Hero = styled.div`
  position: relative;
  padding: 12rem 0 32rem 0;
  margin-top: -80px;
  color: white;
  overflow: visible;
  z-index: 5;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      #4f46e5 0%,
      #7c3aed 100%
    );
    background-size: 400% 400%;
    animation: ${moveBackground} 15s ease infinite;
    z-index: -1;
    
    clip-path: polygon(
      0 0,
      100% 0,
      100% calc(100% - 600px),
      0 100%
    );
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.15) 0%,
      rgba(0, 0, 0, 0) 100%
    );
    z-index: 0;
    
    clip-path: polygon(
      0 0,
      100% 0,
      100% calc(100% - 600px),
      0 100%
    );
  }
  
  @media (max-width: 1024px) {
    padding: 11rem 0 28rem 0;
    
    &::before, &::after {
      clip-path: polygon(
        0 0,
        100% 0,
        100% calc(100% - 500px),
        0 100%
      );
    }
  }
  
  @media (max-width: 768px) {
    padding: 10rem 0 24rem 0;
    
    &::before, &::after {
      clip-path: polygon(
        0 0,
        100% 0,
        100% calc(100% - 350px),
        0 100%
      );
    }
  }
`;

const HeroContainer = styled.div`
  max-width: 1200px;
  width: 90%;
  margin: 0 auto;
  position: relative;
  z-index: 10;
  padding-bottom: 4rem;
`;

const ServTrackrLogo = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  font-weight: 700;
  margin-bottom: 2rem;
  z-index: 2;
  
  .logo-text {
    font-size: 1.5rem;
    color: white;
    letter-spacing: 0.5px;
  }
  
  .pay-text {
    font-size: 1.5rem;
    background: rgba(5, 150, 105, 0.2);
    backdrop-filter: blur(5px);
    padding: 0.25rem 0.75rem;
    border-radius: 6px;
    margin-left: 0.5rem;
    color: #10b981;
    border: 1px solid rgba(16, 185, 129, 0.4);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    font-weight: 700;
  }
  
  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

const HeroHeading = styled.h1`
  font-size: 5rem;
  font-weight: 800;
  line-height: 1.1;
  margin: 0 0 0.5rem 0;
  position: relative;
  z-index: 2;
  
  @media (max-width: 1024px) {
    font-size: 3.5rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    text-align: center;
  }
`;

const MovingText = styled.div`
  position: relative;
  overflow: hidden;
  height: 8rem;
  margin: 0 0 3rem 0;
  z-index: 2;
  
  @media (max-width: 1024px) {
    height: 6rem;
  }
  
  @media (max-width: 768px) {
    height: 5rem;
    text-align: center;
    margin-bottom: 2rem;
  }

  @media (max-width: 480px) {
    height: 3.5rem;
  }
`;

const MovingWord = styled(motion.span)`
  display: block;
  position: absolute;
  font-size: 6rem;
  font-weight: 700;
  opacity: 0;
  width: 100%;
  left: 0;
  line-height: 1.1;
  
  @media (max-width: 1024px) {
    font-size: 4rem;
  }
  
  @media (max-width: 768px) {
    font-size: 3rem;
    text-align: center;
  }
  
  @media (max-width: 480px) {
    font-size: 2.25rem;
  }
`;

const HeroContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4rem;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 3rem;
  }
  
  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
    gap: 2rem;
  }
`;

const HeroTextContent = styled(motion.div)`
  flex: 1;
  max-width: 550px;
  
  p {
    font-size: 1.25rem;
    line-height: 1.6;
    margin-bottom: 1rem;
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    text-align: center;
    
    p {
      font-size: 1.125rem;
      margin-bottom: 1rem;
    }
  }
`;

const heroImages = [
  "https://images.unsplash.com/photo-1504307651254-35680f356dfd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1581094794329-c8112a89af12?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
  "https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
];

const HeroImage = styled(motion.div)`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  position: relative;
  height: 450px;
  
  img {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 16px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    object-fit: cover;
    object-position: center;
    border: 1px solid rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
  }
  
  @media (max-width: 1024px) {
    justify-content: center;
    width: 100%;
    height: 350px;
  }

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const ApplyButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: #10b981;
  color: white;
  font-weight: 700;
  padding: 1rem 2.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-size: 1.25rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    background: #059669;
    box-shadow: 0 6px 15px rgba(16, 185, 129, 0.4);
  }
  
  svg {
    font-size: 1.25rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
    padding: 0.875rem 2rem;
  }
`;

const ServingSection = styled.section`
  background: white;
  padding: 9rem 0 12rem;
  position: relative;
  margin-top: -100px;
  z-index: 1;
  padding-top: 12rem;
  
  @media (max-width: 768px) {
    padding: 9rem 0 14rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 5.5rem;
  
  h2 {
    font-size: 3rem;
    font-weight: 800;
    color: #111827;
    margin-bottom: 1.5rem;
    letter-spacing: -0.02em;
    
    @media (max-width: 768px) {
      font-size: 2.25rem;
    }
  }
  
  p {
    font-size: 1.25rem;
    color: #4b5563;
    max-width: 650px;
    margin: 0 auto;
    line-height: 1.6;
    
    @media (max-width: 768px) {
      font-size: 1.125rem;
    }
  }
`;

const ServingContainer = styled.div`
  max-width: 1200px;
  width: 90%;
  margin: 0 auto;
`;

const ServingTabs = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 4rem;
  gap: 1.5rem;
  position: relative;
  z-index: 5;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }
`;

const ServingTab = styled.button`
  padding: 1rem 2rem;
  border: none;
  background: ${props => props.active ? '#4f46e5' : 'white'};
  color: ${props => props.active ? 'white' : '#4b5563'};
  font-weight: 600;
  font-size: 1.125rem;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.active ? 
    '0 10px 25px -5px rgba(79, 70, 229, 0.4)' : 
    '0 4px 10px rgba(0, 0, 0, 0.05)'};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  &:hover {
    background: ${props => props.active ? '#4338ca' : 'white'};
    color: ${props => props.active ? 'white' : '#4f46e5'};
    transform: translateY(-3px);
    box-shadow: 0 15px 30px -10px rgba(0, 0, 0, 0.1);
  }
  
  svg {
    font-size: 1.5rem;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 300px;
    padding: 0.875rem 1.75rem;
    justify-content: center;
  }
`;

const ServingContent = styled(motion.div)`
  display: flex;
  align-items: stretch;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 40px rgba(0, 0, 0, 0.08);
  position: relative;
  z-index: 5;
  
  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const ServingIconArea = styled.div`
  flex: 0 0 30%;
  background: linear-gradient(135deg, rgba(79, 70, 229, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 3rem 2rem;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 1px;
    height: 100%;
    background: linear-gradient(to bottom, transparent, rgba(79, 70, 229, 0.1), transparent);
  }
  
  @media (max-width: 1024px) {
    padding: 2rem;
    
    &::after {
      width: 100%;
      height: 1px;
      background: linear-gradient(to right, transparent, rgba(79, 70, 229, 0.1), transparent);
      top: auto;
      bottom: 0;
    }
  }
`;

const IconWrapper = styled(motion.div)`
  width: 120px;
  height: 120px;
  border-radius: 24px;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(79, 70, 229, 0.1);
  
  svg {
    font-size: 3.5rem;
    color: #4f46e5;
  }
  
  @media (max-width: 1024px) {
    width: 100px;
    height: 100px;
    border-radius: 20px;
    
    svg {
      font-size: 3rem;
    }
  }
`;

const ServingTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  text-align: center;
  margin: 0;
`;

const ServingDetailsArea = styled.div`
  flex: 1;
  padding: 3.5rem;
  
  @media (max-width: 1024px) {
    padding: 2.5rem;
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const ServingDescription = styled(motion.p)`
  font-size: 1.125rem;
  color: #4b5563;
  line-height: 1.7;
  margin-bottom: 2.5rem;
  max-width: 90%;
  
  @media (max-width: 1024px) {
    max-width: 100%;
  }
`;

const ServingFeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FeatureItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  
  svg {
    background: rgba(16, 185, 129, 0.1);
    color: #10b981;
    font-size: 1rem;
    padding: 0.5rem;
    border-radius: 50%;
    flex-shrink: 0;
    box-shadow: 0 5px 10px rgba(16, 185, 129, 0.15);
  }
  
  span {
    font-size: 1.0625rem;
    color: #1f2937;
    line-height: 1.5;
    padding-top: 0.25rem;
    font-weight: 500;
  }
`;

const FeaturesSection = styled.section`
  padding: 0 0 8rem 0;
  background: white;
  position: relative;
  overflow: hidden;
  margin-top: -100px;
  padding-top: 12rem;
  
  @media (max-width: 768px) {
    margin-top: -50px;
    padding-top: 9rem;
  }
`;

const FeaturesSectionHeader = styled(SectionHeader)`
  position: relative;
  z-index: 2;
  margin-bottom: 7rem;
  
  h2 {
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      bottom: -12px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 4px;
      background: linear-gradient(90deg, #4f46e5, #7c3aed);
      border-radius: 2px;
    }
  }
  
  p {
    max-width: 800px;
    margin-top: 2rem;
  }

  @media (max-width: 768px) {
    margin-bottom: 5rem;
  }
`;

const FeatureRow = styled.div`
  display: flex;
  align-items: center;
  max-width: 1200px;
  width: 90%;
  margin: 0 auto 8rem;
  gap: 6rem;
  position: relative;
  padding: 4rem 2rem;
  border-radius: 24px;
  background: white;
  box-shadow: 0 4px 40px rgba(0, 0, 0, 0.03);
  opacity: 0;
  transform: translateY(30px);
  animation: fadeUp 0.8s ease forwards;
  animation-play-state: paused;
  
  &.visible {
    animation-play-state: running;
  }
  
  @keyframes fadeUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:nth-child(even) {
    flex-direction: row-reverse;
  }
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 24px;
    background: linear-gradient(135deg, rgba(79, 70, 229, 0.05), rgba(124, 58, 237, 0.05));
  }
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 4rem;
    margin-bottom: 6rem;
    padding: 3rem 1.5rem;
    
    &:nth-child(even) {
      flex-direction: column;
    }
  }
`;

const FeatureContent = styled(motion.div)`
  flex: 1;
  
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #4f46e5;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    svg {
      font-size: 1.5rem;
    }
  }
  
  h2 {
    font-size: 2.5rem;
    font-weight: 800;
    color: #111827;
    margin-bottom: 1.5rem;
    line-height: 1.2;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.125rem;
    color: #4b5563;
    margin-bottom: 2.5rem;
    line-height: 1.7;
    
    @media (max-width: 768px) {
      font-size: 1.0625rem;
    }
  }
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const BenefitItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  svg {
    background: rgba(79, 70, 229, 0.1);
    color: #4f46e5;
    padding: 0.5rem;
    border-radius: 8px;
    font-size: 1.25rem;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(79, 70, 229, 0.12);
    transition: all 0.3s ease;
    width: 2rem;
    height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  &:hover svg {
    transform: scale(1.05);
    background: rgba(79, 70, 229, 0.15);
    box-shadow: 0 6px 16px rgba(79, 70, 229, 0.2);
  }
  
  span {
    font-size: 1.125rem;
    color: #374151;
    font-weight: 500;
    line-height: 1.5;
  }
`;

const LearnMoreLink = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  color: #4f46e5;
  font-weight: 600;
  text-decoration: none;
  font-size: 1.125rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  background: rgba(79, 70, 229, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(79, 70, 229, 0.15);
    gap: 1rem;
  }
  
  svg {
    font-size: 1.25rem;
  }
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  padding: 6rem 0;
  color: white;
  text-align: center;
`;

const CTAContent = styled.div`
  max-width: 900px;
  width: 90%;
  margin: 0 auto;
  
  h2 {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    
    @media (max-width: 768px) {
      font-size: 2.25rem;
    }
  }
  
  p {
    font-size: 1.25rem;
    margin-bottom: 2.5rem;
    opacity: 0.9;
    
    @media (max-width: 768px) {
      font-size: 1.125rem;
    }
  }
`;

const CTAButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: white;
  color: #4f46e5;
  font-weight: 700;
  padding: 1rem 2.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-size: 1.25rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f9fafb;
    gap: 1rem;
  }
  
  svg {
    font-size: 1.25rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
    padding: 0.875rem 2rem;
  }
`;

const movingWords = ["efficiency.", "payments.", "growth.", "commerce."];

const servingData = [
  {
    title: "Small Businesses",
    icon: <Storefront weight="duotone" />,
    phosphorIcon: <Storefront size={28} weight="duotone" />,
    description: "All-in-one systems to accept payments and manage your service business with intuitive tools designed for growing companies.",
    features: [
      "Mobile and contactless payments",
      "Integrated scheduling and dispatch",
      "Customer management tools",
      "Real-time reporting"
    ]
  },
  {
    title: "Mid-Size Companies",
    icon: <Buildings weight="duotone" />,
    phosphorIcon: <Buildings size={28} weight="duotone" />,
    description: "Scalable solutions that grow with your business needs, providing powerful analytics and multi-location support.",
    features: [
      "Multichannel payment acceptance",
      "Advanced reporting and analytics",
      "Custom pricing options",
      "Dedicated account management"
    ]
  },
  {
    title: "Enterprise Organizations",
    icon: <Bank weight="duotone" />,
    phosphorIcon: <Bank size={28} weight="duotone" />,
    description: "Omnichannel commerce and payment solutions for larger operations with enterprise-grade security and compliance.",
    features: [
      "Enterprise-grade security",
      "Custom integration capabilities",
      "Global payment processing",
      "Advanced fraud prevention"
    ]
  }
];

const features = [
  {
    title: "Unified Payment Platform",
    subtitle: "All-In-One Solution",
    description: "Connect all your payment channels in one unified platform. ServTrackr Pay seamlessly integrates with your existing workflows, offering a consistent payment experience across all touchpoints.",
    image: "https://images.unsplash.com/photo-1556742393-d75f468bfcb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
    benefits: [
      "Support for 20+ payment methods",
      "Seamless integration with ServTrackr",
      "Automated reconciliation",
      "Custom payment pages"
    ]
  },
  {
    title: "Advanced Security",
    subtitle: "Enterprise Protection",
    description: "Keep financial data ultra-secure with our enterprise-grade security infrastructure. ServTrackr Pay exceeds industry standards with advanced fraud detection and encryption.",
    image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
    benefits: [
      "PCI DSS Level 1 certified",
      "AI-powered fraud detection",
      "End-to-end encryption",
      "Secure customer vault"
    ]
  },
  {
    title: "Business Intelligence",
    subtitle: "Data-Driven Insights",
    description: "Transform payment data into actionable business intelligence. Our analytics dashboard gives you complete visibility into your revenue streams with customizable reports.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80",
    benefits: [
      "Real-time revenue tracking",
      "Customer payment analysis",
      "Revenue forecasting",
      "Exportable custom reports"
    ]
  }
];

const processorLogos = [
  "https://cdn.worldvectorlogo.com/logos/visa-10.svg",
  "https://cdn.worldvectorlogo.com/logos/mastercard-modern-design-.svg",
  "https://cdn.worldvectorlogo.com/logos/american-express-1.svg",
  "https://cdn.worldvectorlogo.com/logos/venmo.svg",
  "https://cdn.worldvectorlogo.com/logos/paypal-3.svg",
  "https://cdn.worldvectorlogo.com/logos/apple-pay-2.svg",
  "https://cdn.worldvectorlogo.com/logos/google-pay-2.svg"
];

const ProcessorCarousel = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 120%;
  max-width: 1800px;
  height: 180px;
  bottom: -400px;
  margin-top: 8rem;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 6;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08);
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    height: 100%;
    width: 120px;
    z-index: 2;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, rgba(255, 255, 255, 0.95), transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, rgba(255, 255, 255, 0.95), transparent);
  }
`;

const slideAnimation = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const ProcessorTrack = styled.div`
  display: flex;
  gap: 3.5rem;
  align-items: center;
  animation: ${slideAnimation} 40s linear infinite;
  width: fit-content;
  padding: 0 2rem;

  &:hover {
    animation-play-state: paused;
  }
`;

const ProcessorLogo = styled(motion.div)`
  border-radius: 12px;
  padding: 1.25rem;
  width: 120px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(229, 231, 235, 0.5);
  
  &:hover {
    transform: translateY(-8px) scale(1.05);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
    border-color: rgba(79, 70, 229, 0.2);
  }
  
  img {
    width: 85%;
    height: auto;
    object-fit: contain;
    max-height: 85%;
  }

  &.apple-pay img {
    width: 100%;
    max-height: 100%;
  }
  
  @media (max-width: 768px) {
    width: 100px;
    height: 70px;
    padding: 1rem;
  }
`;

const FeatureImage = styled(motion.div)`
  flex: 1;
  position: relative;
  z-index: 1;
  
  img {
    width: 100%;
    border-radius: 16px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(0, 0, 0, 0.05);
    object-fit: cover;
    height: 400px;
  }
  
  &::before {
    content: '';
    position: absolute;
    inset: -15px;
    border-radius: 24px;
    background: linear-gradient(135deg, rgba(79, 70, 229, 0.1), rgba(124, 58, 237, 0.1));
    z-index: -1;
  }
  
  &::after {
    content: '';
    position: absolute;
    inset: -5px;
    border-radius: 20px;
    background: white;
    z-index: -1;
  }

  @media (max-width: 1024px) {
    img {
      height: 300px;
    }
  }

  @media (max-width: 768px) {
    img {
      height: 250px;
    }
  }
`;

const Payments = () => {
  const [wordIndex, setWordIndex] = React.useState(0);
  const [imageIndex, setImageIndex] = React.useState(0);
  const [activeTab, setActiveTab] = React.useState(0);
  const [isPaused, setIsPaused] = React.useState(false);
  
  useEffect(() => {
    const wordInterval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % movingWords.length);
    }, 3000);
    
    const imageInterval = setInterval(() => {
      setImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    
    return () => {
      clearInterval(wordInterval);
      clearInterval(imageInterval);
    };
  }, []);
  
  useEffect(() => {
    if (isPaused) return;
    
    const tabInterval = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % servingData.length);
    }, 5000);
    
    return () => {
      clearInterval(tabInterval);
    };
  }, [isPaused]);
  
  const pageTitle = "ServTrackr Pay | Secure Payment Processing";
  const pageDescription = "Streamline payments with ServTrackr Pay. Secure, integrated payment processing for field service businesses. Accept cards, ACH, and more.";
  const pageImage = "https://servtrackr.com/images/payments-preview.png"; // <-- REPLACE with actual image URL
  const pageUrl = "https://servtrackr.com/features/payments"; // <-- REPLACE with actual URL

  return (
    <PageContainer>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {/* Open Graph / Facebook */} 
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />
        {/* Twitter */} 
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />
      </Helmet>
      <Hero>
        <HeroContainer>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <ServTrackrLogo>
              <span className="logo-text">ServTrackr</span>
              <span className="pay-text">Pay</span>
            </ServTrackrLogo>
          </motion.div>
          
          <HeroHeading>Moving field service</HeroHeading>
          
          <MovingText>
            {movingWords.map((word, index) => (
              <MovingWord
                key={word}
                initial={{ opacity: 0, y: 50 }}
                animate={{ 
                  opacity: wordIndex === index ? 1 : 0,
                  y: wordIndex === index ? 0 : 50
                }}
                exit={{ opacity: 0, y: -50 }}
                transition={{ duration: 0.5 }}
              >
                {word}
              </MovingWord>
            ))}
          </MovingText>
          
          <HeroContent>
            <HeroTextContent
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <p>
                Every minute of the day, service businesses are connecting with customers
                through ServTrackr Pay's secure payment processing technology.
              </p>
              
              <ApplyButton
                href="/apply"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Apply Now
                <ArrowForward />
              </ApplyButton>
            </HeroTextContent>
            
            <HeroImage
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {heroImages.map((src, index) => (
                <motion.img
                  key={src}
                  src={src}
                  alt={`ServTrackr Pay in action ${index + 1}`}
                  initial={{ opacity: 0 }}
                  animate={{ 
                    opacity: imageIndex === index ? 1 : 0,
                    scale: imageIndex === index ? 1 : 1.1
                  }}
                  transition={{ 
                    opacity: { duration: 0.8 },
                    scale: { duration: 5 }
                  }}
                  style={{ 
                    position: 'absolute',
                    display: imageIndex === index ? 'block' : 'none'
                  }}
                />
              ))}
            </HeroImage>
          </HeroContent>
          
          <ProcessorCarousel>
            <ProcessorTrack>
              {[...processorLogos, ...processorLogos, ...processorLogos, ...processorLogos, ...processorLogos, ...processorLogos, ...processorLogos].map((logo, index) => (
                <ProcessorLogo
                  key={index}
                  whileHover={{ y: -8 }}
                  className={logo.includes('apple-pay') ? 'apple-pay' : ''}
                >
                  <img src={logo} alt={`Payment processor ${index % processorLogos.length + 1}`} />
                </ProcessorLogo>
              ))}
            </ProcessorTrack>
          </ProcessorCarousel>
        </HeroContainer>
      </Hero>
      
      <ServingSection>
        <SectionHeader>
          <h2>Who We Serve</h2>
          <p>Tailored payment processing solutions designed for businesses across the service industry spectrum</p>
        </SectionHeader>
        
        <ServingContainer>
          <ServingTabs>
            {servingData.map((item, index) => (
              <ServingTab 
                key={index}
                active={activeTab === index}
                onClick={() => {
                  setActiveTab(index);
                  setIsPaused(true);
                  setTimeout(() => setIsPaused(false), 15000);
                }}
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
              >
                {item.phosphorIcon}
                {item.title}
              </ServingTab>
            ))}
          </ServingTabs>
          
          <AnimatePresence mode="wait">
            <ServingContent
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <ServingIconArea>
                <IconWrapper
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  {servingData[activeTab].icon}
                </IconWrapper>
                <ServingTitle>{servingData[activeTab].title}</ServingTitle>
              </ServingIconArea>
              
              <ServingDetailsArea>
                <ServingDescription
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  {servingData[activeTab].description}
                </ServingDescription>
                
                <ServingFeatureGrid>
                  {servingData[activeTab].features.map((feature, i) => (
                    <FeatureItem
                      key={i}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.4 + (i * 0.1) }}
                    >
                      <Check />
                      <span>{feature}</span>
                    </FeatureItem>
                  ))}
                </ServingFeatureGrid>
              </ServingDetailsArea>
            </ServingContent>
          </AnimatePresence>
        </ServingContainer>
      </ServingSection>
      
      <FeaturesSection>
        <FeaturesSectionHeader>
          <h2>Driving Payment Innovation</h2>
          <p>Discover how ServTrackr Pay helps service businesses streamline operations and increase revenue</p>
        </FeaturesSectionHeader>
        
        {features.map((feature, index) => (
          <FeatureRow 
            key={index} 
            index={index}
            as={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ 
              duration: 0.8,
              delay: index * 0.2,
              ease: "easeOut"
            }}
          >
            <FeatureImage
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 + (index * 0.2) }}
            >
              <img src={feature.image} alt={feature.title} />
            </FeatureImage>
            
            <FeatureContent
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 + (index * 0.2) }}
            >
              <h3>{feature.subtitle}</h3>
              <h2>{feature.title}</h2>
              <p>{feature.description}</p>
              
              <BenefitsList>
                {feature.benefits.map((benefit, i) => (
                  <BenefitItem
                    key={i}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.6 + (index * 0.2) + (i * 0.1) }}
                  >
                    <Check />
                    <span>{benefit}</span>
                  </BenefitItem>
                ))}
              </BenefitsList>
              
              <LearnMoreLink
                href={`/features/payments/${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Learn more <ArrowForward />
              </LearnMoreLink>
            </FeatureContent>
          </FeatureRow>
        ))}
      </FeaturesSection>
      
      <CTASection>
        <CTAContent>
          <h2>Ready to streamline your payments?</h2>
          <p>Join thousands of service businesses that trust ServTrackr Pay for secure, reliable payment processing. Get started in minutes with no long-term commitments.</p>
          
          <CTAButton
            href="/contact"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            Get Started Today <ArrowForward />
          </CTAButton>
        </CTAContent>
      </CTASection>
    </PageContainer>
  );
};

export default Payments;
